import React, { HTMLProps, useEffect, useRef } from 'react';
import { DisplayError } from './DisplayError';
import { LoadingSpinner } from './LoadingSpinner';

export type LoadingStateWrapperProperties = {
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    isIdle: boolean;
    errorElement?: React.ReactNode;
    loadingElement?: React.ReactNode;
};

export const LoadingStateWrapper: React.FC<LoadingStateWrapperProperties & HTMLProps<HTMLDivElement>> = ({
    isLoading,
    isFetching,
    isError,
    isIdle,
    loadingElement = <LoadingSpinner />,
    errorElement = <DisplayError />,
    className,
    children,
    ...rest
}) => {
    return (
        <div className={`relative ${className}`} {...rest}>
            <LoadingState isLoading={isLoading} loadingElement={loadingElement} />
            <ErrorState isLoading={isLoading} isError={isError} errorElement={errorElement} />
            <SuccesState isLoading={isLoading} isFetching={isFetching} isError={isError} isIdle={isIdle}>
                {children as React.ReactNode}
            </SuccesState>
        </div>
    );
};

const LoadingState: React.FC<{ isLoading: boolean; loadingElement: React.ReactNode }> = ({
    isLoading,
    loadingElement,
}) => (
    <div
        className={`absolute inset-0 flex items-center justify-center transition ${
            isLoading ? '' : 'opacity-0 pointer-events-none'
        }`}
    >
        {loadingElement}
    </div>
);

const ErrorState: React.FC<{ isLoading: boolean; isError: boolean; errorElement: React.ReactNode }> = ({
    isLoading,
    isError,
    errorElement,
}) => {
    const showError = isError && !isLoading;
    return (
        <div
            className={`absolute inset-0 flex items-center justify-center transition ${
                showError ? '' : 'opacity-0 pointer-events-none'
            } `}
        >
            {errorElement}
        </div>
    );
};

const SuccesState: React.FC<{
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    isIdle: boolean;
    children?: React.ReactNode;
}> = ({ isLoading, isFetching, isError, isIdle, children }) => {
    const showChildren = !isError && !isLoading;
    const pulseChildren = isFetching && showChildren;

    const divRef = useRef<HTMLDivElement>(undefined!);

    const animHandler = (ev: React.AnimationEvent<HTMLDivElement>) => {
        if (!pulseChildren) {
            ev.currentTarget.classList.remove('animate-pulse-fast');
        }
    };
    useEffect(() => {
        if (pulseChildren) {
            divRef.current.classList.add('animate-pulse-fast');
        }
    }, [divRef, pulseChildren]);

    return (
        <div
            ref={divRef}
            onAnimationIteration={animHandler}
            className={`h-full transition ${showChildren ? '' : 'opacity-0 pointer-events-none'} ${
                showChildren ? 'animate-pulse-fast' : ''
            }`}
        >
            {children}
        </div>
    );
};
